import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { environment } from '@journal/env/environment';
import { AiEditsComponent } from '@journal/app/modules/journal-edit/ai-edits/ai-edits.component';
import { MatDialog } from '@angular/material/dialog';

@Injectable({
  providedIn: 'root'
})
export class JournalService {
  private baseUrl = `${environment.baseUrl}`;
  textCount: number = 0;
  blog = new BehaviorSubject(null);
  canEditUsingAi = new BehaviorSubject(false);
  static instance: JournalService;
  private referneceDialog = new Subject<string>();

  constructor(private http: HttpClient, private dialog: MatDialog) {
    this.baseUrl = `${environment.serverUrl}${this.baseUrl}`;
    JournalService.instance = this;
  }

  referneceDialog$ = this.referneceDialog.asObservable();

  openReferenceDialog() {
    this.referneceDialog.next();
  }

  getBlogs(page: number) {
    return this.http.get(this.baseUrl + 'blogs/?page=' + page);
  }

  getFeaturedBlogs() {
    return this.http.get(this.baseUrl + 'blogs/featured/');
  }

  getB2BBlogs() {
    return this.http.get(this.baseUrl + 'blogs/on_b2b/');
  }

  getTrendingBlogs() {
    return this.http.get(this.baseUrl + 'blogs/trending/');
  }

  getBlogTypeCompetitions() {
    return this.http.get(
      this.baseUrl +
        'competitions/ongoing_registration/?page=1&ordering=submission_end_date&entry_type=blog'
    );
  }

  getFeedBlogs(page: number, blogTypeId: number = null) {
    return this.http.get(
      this.baseUrl + 'blogs/feed/?page=' + page + (blogTypeId ? `&blogTypeId=${blogTypeId}` : '')
    );
  }

  getBlogsByUserId(page: number, userId: number) {
    return this.http.get(this.baseUrl + 'blogs/?page=' + page + '&user_id=' + userId);
  }

  getBlogDetail(slug: string) {
    return this.http.get(this.baseUrl + 'blogs/' + slug + '/');
  }

  getPaginatedBlog(pageUrl: string) {
    return this.http.get(pageUrl);
  }

  createBlog(postData) {
    return this.http.post(this.baseUrl + 'blog/self/', postData);
  }

  getBlog(uuid: string) {
    return this.http.get(this.baseUrl + 'blog/self/' + uuid + '/');
  }

  updateBlog(uuid: string, data) {
    return this.http.put(this.baseUrl + 'blog/self/' + uuid + '/', data);
  }

  deleteBlog(uuid: string) {
    return this.http.delete(this.baseUrl + 'blog/self/' + uuid + '/');
  }

  uploadBlogCover(formData) {
    return this.http.post(this.baseUrl + 'blog_cover_upload/', formData, {
      reportProgress: true,
      observe: 'events'
    });
  }

  publishBlog(uuid: string) {
    return this.http.put(this.baseUrl + 'blog/self/' + uuid + '/publish/', {});
  }

  searchTag(search_text: string) {
    return this.http.get(this.baseUrl + 'tags/?search_text=' + search_text);
  }

  searchSuperTag(search_text: string) {
    return this.http.get(this.baseUrl + 'super_tags/?search_text=' + search_text);
  }

  addTag(uuid: string, data) {
    return this.http.put(this.baseUrl + 'blog/self/' + uuid + '/add_tag/', data);
  }

  removeTag(uuid: string, data) {
    return this.http.put(this.baseUrl + 'blog/self/' + uuid + '/remove_tag/', data);
  }

  getBlogTypes() {
    return this.http.get(this.baseUrl + 'super_tags/?type=Blog Type');
  }

  changeBlogType(uuid: string, data) {
    return this.http.put(this.baseUrl + 'blog/self/' + uuid + '/change_type/', data);
  }

  allDisciplines() {
    return this.http.get(this.baseUrl + 'super_tags/all_disciplines/');
  }

  addDiscipline(uuid: string, data) {
    return this.http.put(this.baseUrl + 'blog/self/' + uuid + '/add_discipline/', data);
  }

  removeDiscipline(uuid: string, data) {
    return this.http.put(this.baseUrl + 'blog/self/' + uuid + '/remove_discipline/', data);
  }

  connectDisciplineThumbnail(uuid: string, data) {
    return this.http.put(
      this.baseUrl + 'blog/self/' + uuid + '/connect_discipline_thumbnail/',
      data
    );
  }

  removeDisciplineThumbnail(uuid: string) {
    return this.http.put(this.baseUrl + 'blog/self/' + uuid + '/remove_discipline_thumbnail/', {});
  }

  checkUserBlogLike(slug: string) {
    return this.http.get(this.baseUrl + 'blogs/' + slug + '/check_liked/');
  }

  likeBlog(slug: string) {
    return this.http.put(this.baseUrl + 'blogs/' + slug + '/like/', {});
  }

  createReference(uuid: string, data) {
    return this.http.post(this.baseUrl + 'blog/self/' + uuid + '/references/', data);
  }

  updateReference(uuid: string, reference_id: number, data) {
    return this.http.put(
      this.baseUrl + 'blog/self/' + uuid + '/references/' + reference_id + '/',
      data
    );
  }

  deleteReference(uuid: string, reference_id: number) {
    return this.http.delete(
      this.baseUrl + 'blog/self/' + uuid + '/references/' + reference_id + '/',
      {}
    );
  }

  similarBlogs(slug: string) {
    return this.http.get(this.baseUrl + 'blogs/' + slug + '/similar_blogs/');
  }

  getSeoContent() {
    return this.http.get(this.baseUrl + 'seo_content/blog/');
  }

  getAd(host: string) {
    return this.http.get(this.baseUrl + 'advertisement/?host=' + host);
  }

  getEditorialBlogs(page: number, typeId: number = null) {
    return this.http.get(
      `${this.baseUrl}blogs/?page=${page}&editorial=true&types=${typeId ? typeId : ''}`
    );
  }

  getTopBlogSuperTags() {
    return this.http.get(`${this.baseUrl}super_tags/top_blog_supertags/`);
  }

  setBlog(blog) {
    this.blog.next(blog);
  }

  setCanEditUsingAi(canEdit: boolean) {
    this.canEditUsingAi.next(canEdit);
  }

  getCanEditUsingAi() {
    return this.canEditUsingAi;
  }

  getAiEditPermission() {
    return this.http.get(`${this.baseUrl}user_memberships/can_edit_using_ai/`);
  }

  getBlogValue() {
    return this.blog;
  }
  uploadMedia(formData: any) {
    return this.http
      .post(this.baseUrl + 'blog_media_upload/', formData)
      .toPromise()
      .then((response: any) => {
        return response;
      })
      .catch((error: any) => {
        console.error(error);
        throw new Error('Something went wrong while uploading image via url.');
      });
  }

  openDialog(range: any) {
    const dialogRef = this.dialog.open(AiEditsComponent, {
      maxWidth: '720px',
      width: '100vw',
      data: {
        range: range
      }
    });

    return dialogRef
      .afterClosed()
      .toPromise()
      .then((response: any) => {
        return response;
      })
      .catch((error: any) => {
        console.error(error);
        throw new Error('Something went wrong. Please try again later.');
      });
  }
}
